import { useState } from "react";
import { TypeAddInput } from "./TypeAddInput";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { X, Briefcase, UserCheck, Check } from "lucide-react";
import { ManagedCampaignData } from "./ManagedCampaignData";

// ... existing imports and interfaces ...

interface ManagedPeopleStepProps {
  data: ManagedCampaignData;
  updateData: (updates: Partial<ManagedCampaignData>) => void;
}

const DEPARTMENTS = [
  "Sales",
  "Marketing",
  "Human Resources",
  "Finance",
  "Engineering",
  "Operations",
  "Product",
  "Customer Success",
  "Business Development",
  "Executive/C-Suite",
];

const SUGGESTED_TITLES = [
  "CEO",
  "CTO",
  "CFO",
  "VP of Sales",
  "Marketing Manager",
  "HR Director",
  "Product Manager",
  "Business Development Manager",
  "Sales Manager",
  "Head of Growth",
];

const DEPARTMENT_TITLES: Record<string, string[]> = {
  "Sales": ["Sales Representative", "Account Executive", "Sales Manager", "VP of Sales", "Sales Director"],
  "Marketing": ["Marketing Manager", "CMO", "Marketing Coordinator", "Brand Manager", "Growth Marketer"],
  "Human Resources": ["HR Manager", "Recruiter", "HR Director", "Chief People Officer", "Talent Acquisition Specialist"],
  "Finance": ["CFO", "Finance Manager", "Controller", "Accountant", "Financial Analyst"],
  "Engineering": ["CTO", "Software Engineer", "Engineering Manager", "VP of Engineering", "DevOps Engineer"],
  "Operations": ["COO", "Operations Manager", "Director of Operations", "Operations Analyst", "Program Manager"],
  "Product": ["Product Manager", "CPO", "Product Owner", "Head of Product", "Associate Product Manager"],
  "Customer Success": ["Customer Success Manager", "CSM", "Head of Customer Success", "Support Manager", "Onboarding Specialist"],
  "Business Development": ["BDR", "SDR", "Head of Business Development", "Partnerships Manager"],
  "Executive/C-Suite": ["CEO", "Founder", "Co-Founder", "Owner", "President"]
};

export const ManagedPeopleStep = ({ data, updateData }: ManagedPeopleStepProps) => {
  const [titleInput, setTitleInput] = useState("");
  const [departmentInput, setDepartmentInput] = useState("");

  const suggestedTitles = data.departments.length > 0
    ? data.departments.flatMap(dept => DEPARTMENT_TITLES[dept] || [])
    : SUGGESTED_TITLES;

  const filteredSuggestedTitles = [...new Set(suggestedTitles)] // Remove duplicates
    .filter(t => !data.jobTitles.includes(t))
    .slice(0, 15); // Limit suggestions

  const toggleDepartment = (dept: string) => {
    if (data.departments.includes(dept)) {
      updateData({ departments: data.departments.filter(d => d !== dept) });
    } else {
      updateData({ departments: [...data.departments, dept] });
    }
  };

  const addDepartment = () => {
    const dept = departmentInput.trim();
    if (dept && !data.departments.includes(dept)) {
      updateData({ departments: [...data.departments, dept] });
      setDepartmentInput("");
    }
  };

  const handleDepartmentKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      addDepartment();
    }
  };

  const addTitle = (title?: string) => {
    const titleToAdd = (title || titleInput).trim();
    if (titleToAdd && !data.jobTitles.includes(titleToAdd)) {
      updateData({ jobTitles: [...data.jobTitles, titleToAdd] });
      setTitleInput("");
    }
  };

  const removeTitle = (title: string) => {
    updateData({ jobTitles: data.jobTitles.filter(t => t !== title) });
  };

  const handleTitleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      addTitle();
    }
  };

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
      {/* Departments */}
      <Card>
        <CardContent className="pt-6">
          <div className="space-y-3">
            <div className="flex items-center gap-2">
              <Briefcase className="w-4 h-4 text-muted-foreground" />
              <Label className="text-sm font-medium">
                Departments <span className="text-muted-foreground text-xs">(optional)</span>
              </Label>
            </div>
            <p className="text-xs text-muted-foreground">
              Which departments should your prospects work in?
            </p>

            {/* Selected Departments Chips */}
            {data.departments.length > 0 && (
              <div className="flex flex-wrap gap-2 mb-2">
                {data.departments.map(dept => (
                  <Badge
                    key={dept}
                    variant="default"
                    className="gap-1 pl-2.5 pr-2 py-1.5 bg-success text-success-foreground hover:bg-success/90 border-transparent shadow-sm"
                  >
                    <Check className="w-3 h-3" />{dept}
                    <X
                      className="w-3 h-3 cursor-pointer hover:opacity-70"
                      onClick={() => toggleDepartment(dept)}
                    />
                  </Badge>
                ))}
              </div>
            )}

            <TypeAddInput placeholder="Type any department, e.g. Finance" value={departmentInput} onChange={setDepartmentInput} onKeyDown={handleDepartmentKeyDown} onAdd={() => addDepartment()} />

            {/* Popular Suggestions */}
            <div className="pt-2">
              <p className="text-xs text-muted-foreground mb-2">Popular departments:</p>
              <div className="flex flex-wrap gap-2">
                {DEPARTMENTS.filter(d => !data.departments.includes(d)).map(dept => (
                  <Badge
                    key={dept}
                    variant="outline"
                    className="cursor-pointer hover:bg-primary/10 hover:text-primary hover:border-primary/30 transition-colors px-3 py-1.5 font-normal"
                    onClick={() => toggleDepartment(dept)}
                  >
                    + {dept}
                  </Badge>
                ))}
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="pt-6">
          <div className="space-y-3">
            <div className="flex items-center gap-2">
              <UserCheck className="w-4 h-4 text-muted-foreground" />
              <Label className="text-sm font-medium">
                Specific Job Titles <span className="text-destructive">*</span>
              </Label>
            </div>

            {/* Selected Job Titles Chips */}
            {data.jobTitles.length > 0 && (
              <div className="flex flex-wrap gap-2 mb-2">
                {data.jobTitles.map(title => (
                  <Badge key={title} variant="default" className="gap-1 pl-2.5 pr-2 py-1.5 bg-success text-success-foreground hover:bg-success/90 border-transparent shadow-sm">
                    <Check className="w-3 h-3" />{title}
                    <X
                      className="w-3 h-3 cursor-pointer hover:opacity-70"
                      onClick={() => removeTitle(title)}
                    />
                  </Badge>
                ))}
              </div>
            )}

            <TypeAddInput placeholder="Type any job title, e.g. CFO" value={titleInput} onChange={setTitleInput} onKeyDown={handleTitleKeyDown} onAdd={() => addTitle()} />

            {/* Quick Add Suggestions */}
            <div className="pt-2">
              <p className="text-xs text-muted-foreground mb-2">
                {data.departments.length > 0 ? "Suggested titles (based on selected departments):" : "Quick add:"}
              </p>
              <div className="flex flex-wrap gap-2">
                {filteredSuggestedTitles.map(title => (
                  <Badge
                    key={title}
                    variant="outline"
                    className="cursor-pointer hover:bg-primary/10 hover:text-primary hover:border-primary/30 transition-colors px-3 py-1.5 font-normal"
                    onClick={() => addTitle(title)}
                  >
                    + {title}
                  </Badge>
                ))}
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};
